import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { Icon } from '@platform/ui';
import { routes } from '@platform/core/lib/routes';
import { cx } from '@platform/core/lib/cx';
import { moduleNav, modulesLink } from '@platform/navigation';
import { FloatingMenu } from './FloatingMenu';
import type { IMenuRow } from './IMenuRow';
import type { IPinnedMenuProps } from './IPinnedMenuProps';
import { menuAnchor } from './menuAnchor';
import { moduleCount } from './moduleCount';
import { pinnedKeys } from './pinnedKeys';
import { useShellNav } from './useShellNav';

/**
 * Закріплені модулі (UIUX top-bar: зірка в кінці смуги груп, прототип v4): кругла кнопка 34px із заповненою зіркою відкриває меню
 * закріплених модулів (плитка, назва, лічильник) із переходом до «Усі модулі» → «Закріплені».
 */
export function PinnedMenu({ ws, open, onToggle, onClose }: IPinnedMenuProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const nav = useShellNav(ws);
  const trigger = useRef<HTMLElement | null>(null);
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  const keys = pinnedKeys(nav.me, nav.catalog, ws);
  const rows: IMenuRow[] = keys.map((key) => {
    const summary = nav.catalog?.modules.find((m) => m.key === key);
    const meta = moduleNav(key);
    return { key, kind: 'item', label: label(summary?.name) || key, icon: meta.icon, tone: meta.tone, count: moduleCount(summary), to: routes.module(key), active: nav.module === key };
  });
  const title = t('shell.pinned');
  // прототип показує зірку, коли простір має закріплені модулі: вибір користувача або автозакріплення CRM і бази знань (pinnedKeys)
  if (nav.catalog && !keys.length) return null;
  return (
    <>
      <button type="button" className={cx('po-topbar__icon tb-icon tb-star', open && 'is-open')} title={title} aria-label={title} aria-haspopup="menu" aria-expanded={open}
        onClick={(e) => { trigger.current = e.currentTarget; setAnchor(menuAnchor(e.currentTarget)); onToggle(); }}>
        <Icon name="star" size={16} strokeWidth={1.5} filled />
      </button>
      {open && <FloatingMenu label={title} rows={rows} empty={t('shell.pinnedEmpty')} x={anchor.x} y={anchor.y} onClose={onClose} trigger={trigger}
        footer={{ label: t('shell.showInAll'), to: modulesLink('pinned') }} />}
    </>
  );
}
